import { api } from '@/lib/api';
import type {
  AdoptionListing,
  AdoptionListingPayload,
  AdoptionPhoto,
  AdoptionRequest,
  BrowseResponse,
  CreateAdoptionRequestResponse,
  InterestedAdopter,
  MyAdoption,
  SendOtpResponse,
} from './types';

const BASE = '/user/adoption';

// ---- My listings (poster) ----
export async function fetch_my_listings(): Promise<AdoptionListing[]> {
  return (await api.get<AdoptionListing[]>(`${BASE}/listings`)).data;
}
export async function fetch_my_listing(id: string): Promise<AdoptionListing> {
  return (await api.get<AdoptionListing>(`${BASE}/listings/${id}`)).data;
}
export async function create_listing(input: AdoptionListingPayload): Promise<AdoptionListing> {
  return (await api.post<AdoptionListing>(`${BASE}/listings`, input)).data;
}
export async function update_listing(id: string, input: Partial<AdoptionListingPayload>): Promise<AdoptionListing> {
  return (await api.patch<AdoptionListing>(`${BASE}/listings/${id}`, input)).data;
}
export async function delete_listing(id: string): Promise<void> {
  await api.delete(`${BASE}/listings/${id}`);
}
export async function mark_adopted(id: string): Promise<AdoptionListing> {
  return (await api.post<AdoptionListing>(`${BASE}/listings/${id}/mark-adopted`)).data;
}
export async function close_listing(id: string): Promise<AdoptionListing> {
  return (await api.post<AdoptionListing>(`${BASE}/listings/${id}/close`)).data;
}

// ---- Photos ----
export async function fetch_listing_photos(id: string): Promise<AdoptionPhoto[]> {
  return (await api.get<AdoptionPhoto[]>(`${BASE}/listings/${id}/photos`)).data;
}
export async function upload_listing_photo(id: string, file: File): Promise<AdoptionPhoto> {
  const form = new FormData();
  form.append('file', file);
  return (
    await api.post<AdoptionPhoto>(`${BASE}/listings/${id}/photos`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  ).data;
}
export async function delete_listing_photo(id: string, photo_id: string): Promise<void> {
  await api.delete(`${BASE}/listings/${id}/photos/${photo_id}`);
}

// ---- OTP publish ----
export async function send_listing_otp(id: string): Promise<SendOtpResponse> {
  return (await api.post<SendOtpResponse>(`${BASE}/listings/${id}/send-otp`)).data;
}
export async function verify_listing_otp(id: string, code: string): Promise<AdoptionListing> {
  return (await api.post<AdoptionListing>(`${BASE}/listings/${id}/verify-otp`, { code })).data;
}

// ---- Interested adopters (poster) ----
export async function fetch_listing_requests(id: string): Promise<InterestedAdopter[]> {
  return (await api.get<InterestedAdopter[]>(`${BASE}/listings/${id}/requests`)).data;
}

// ---- Public browse ----
export type BrowseFilters = {
  species?: string;
  city?: string;
  q?: string;
  page?: number;
  lat?: number;
  lng?: number;
};
export async function browse_listings(filters: BrowseFilters): Promise<BrowseResponse> {
  const params: Record<string, string | number> = {};
  if (filters.species) params.species = filters.species;
  if (filters.city) params.city = filters.city;
  if (filters.q) params.q = filters.q;
  if (filters.page) params.page = filters.page;
  if (filters.lat != null && filters.lng != null) {
    params.lat = filters.lat;
    params.lng = filters.lng;
  }
  return (await api.get<BrowseResponse>(`${BASE}/browse`, { params })).data;
}
export async function fetch_browse_detail(id: string): Promise<AdoptionListing> {
  return (await api.get<AdoptionListing>(`${BASE}/browse/${id}`)).data;
}

// ---- Adopter requests ----
export async function create_request(listing_id: string): Promise<CreateAdoptionRequestResponse> {
  return (await api.post<CreateAdoptionRequestResponse>(`${BASE}/browse/${listing_id}/request`)).data;
}
export async function pay_request(request_id: string): Promise<AdoptionRequest> {
  return (await api.post<AdoptionRequest>(`${BASE}/requests/${request_id}/pay`)).data;
}
export async function verify_request_payment(
  request_id: string,
  input: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string },
): Promise<AdoptionRequest> {
  return (await api.post<AdoptionRequest>(`${BASE}/requests/${request_id}/verify-payment`, input)).data;
}
export async function fetch_my_adoptions(): Promise<MyAdoption[]> {
  return (await api.get<MyAdoption[]>(`${BASE}/requests`)).data;
}
